/**
 * Должности, используются для сотрудников. Модуль для работы с API
 * @module positions
 */

import { maxPerPage } from "@/constants";

/**
 * Получение списка должностей. Поддерживает пагинацию, поиск и фильтры
 * @param {Object} params - Параметры запроса
 * @returns {Promise} - список должностей
 * @throws {Error} - ошибка при получении данных
 */
export const getList = async (params) => {
  const queryString = new URLSearchParams(params).toString();

  try {
    const response = await fetch(`/api/positions?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение полного списка должностей без пагинации
 * @param {Object} params - Параметры запроса
 * @returns {Promise} - список должностей
 * @throws {Error} - ошибка при получении данных
 */
export const getPositionsOptions = async (params) => {
  const queryString = new URLSearchParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  }).toString();

  try {
    const response = await fetch(`/api/positions?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает должность по UUID
 * @param {string} uuid - UUID должности
 * @returns {Promise} - должность
 * @throws {Error} - ошибка при получении данных
 */
export const getItem = async (uuid) => {
  try {
    const response = await fetch(`/api/position?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.position;
  } catch (err) {
    throw err;
  }
};

/**
 * Создание должности
 * @param {Object} item - Данные должности
 * @returns {Promise} - созданная должность
 * @throws {Error} - ошибка при создании данных
 */
export const createItem = async (item) => {
  try {
    const response = await fetch("/api/position", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.position;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление должности
 * @param {Object} item - Данные должности
 * @returns {Promise} - обновленная должность
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateItem = async (item) => {
  try {
    const response = await fetch("/api/position", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.position;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление должности
 * @param {Object} item - Данные должности
 * @returns {Promise} - удаленная должность
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteItem = async (item) => {
  try {
    const response = await fetch("/api/position", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.position;
  } catch (err) {
    throw err;
  }
};
